import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { FiSearch, FiChevronDown, FiChevronUp, FiEye, FiEyeOff } from 'react-icons/fi';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import HeadersNonAuth from '../../components/base/HeadersNonAuth';
import { validatePurchaseKey } from '../../services/purchaseKeyService';
import signupService from '../../services/signupService';

// Group countries by region
const countryGroups = {
  'Asia Pacific': [
    { code: '+66', country: 'Thailand' },
    { code: '+65', country: 'Singapore' },
    { code: '+60', country: 'Malaysia' },
    { code: '+62', country: 'Indonesia' },
    { code: '+63', country: 'Philippines' },
    { code: '+84', country: 'Vietnam' },
    { code: '+81', country: 'Japan' },
    { code: '+82', country: 'South Korea' },
    { code: '+86', country: 'China' },
    { code: '+852', country: 'Hong Kong' },
    { code: '+886', country: 'Taiwan' },
    { code: '+61', country: 'Australia' },
    { code: '+64', country: 'New Zealand' },
    { code: '+91', country: 'India' },
    { code: '+880', country: 'Bangladesh' },
    { code: '+977', country: 'Nepal' },
    { code: '+975', country: 'Bhutan' },
    { code: '+95', country: 'Myanmar' },
    { code: '+856', country: 'Laos' },
    { code: '+855', country: 'Cambodia' },
    { code: '+960', country: 'Maldives' },
    { code: '+976', country: 'Mongolia' },
    { code: '+850', country: 'North Korea' },
    { code: '+992', country: 'Tajikistan' },
    { code: '+993', country: 'Turkmenistan' },
    { code: '+994', country: 'Azerbaijan' },
    { code: '+995', country: 'Georgia' },
    { code: '+996', country: 'Kyrgyzstan' },
    { code: '+998', country: 'Uzbekistan' },
    { code: '+670', country: 'East Timor' },
    { code: '+673', country: 'Brunei' },
    { code: '+674', country: 'Nauru' },
    { code: '+675', country: 'Papua New Guinea' },
    { code: '+676', country: 'Tonga' },
    { code: '+677', country: 'Solomon Islands' },
    { code: '+678', country: 'Vanuatu' },
    { code: '+679', country: 'Fiji' },
    { code: '+680', country: 'Palau' },
    { code: '+681', country: 'Wallis and Futuna' },
    { code: '+682', country: 'Cook Islands' },
    { code: '+683', country: 'Niue' },
    { code: '+685', country: 'Samoa' },
    { code: '+686', country: 'Kiribati' },
    { code: '+687', country: 'New Caledonia' },
    { code: '+688', country: 'Tuvalu' },
    { code: '+689', country: 'French Polynesia' },
    { code: '+690', country: 'Tokelau' },
    { code: '+691', country: 'Micronesia' },
    { code: '+692', country: 'Marshall Islands' }
  ],
  'North America': [
    { code: '+1', country: 'United States' },
    { code: '+1', country: 'Canada' },
    { code: '+52', country: 'Mexico' },
    { code: '+501', country: 'Belize' },
    { code: '+502', country: 'Guatemala' },
    { code: '+503', country: 'El Salvador' },
    { code: '+504', country: 'Honduras' },
    { code: '+505', country: 'Nicaragua' },
    { code: '+506', country: 'Costa Rica' },
    { code: '+507', country: 'Panama' },
    { code: '+1242', country: 'Bahamas' },
    { code: '+1246', country: 'Barbados' },
    { code: '+1264', country: 'Anguilla' },
    { code: '+1268', country: 'Antigua and Barbuda' },
    { code: '+1284', country: 'British Virgin Islands' },
    { code: '+1340', country: 'U.S. Virgin Islands' },
    { code: '+1345', country: 'Cayman Islands' },
    { code: '+1441', country: 'Bermuda' },
    { code: '+1473', country: 'Grenada' },
    { code: '+1649', country: 'Turks and Caicos Islands' },
    { code: '+1664', country: 'Montserrat' },
    { code: '+1670', country: 'Northern Mariana Islands' },
    { code: '+1671', country: 'Guam' },
    { code: '+1758', country: 'Saint Lucia' },
    { code: '+1767', country: 'Dominica' },
    { code: '+1784', country: 'Saint Vincent and the Grenadines' },
    { code: '+1787', country: 'Puerto Rico' },
    { code: '+1809', country: 'Dominican Republic' },
    { code: '+1868', country: 'Trinidad and Tobago' },
    { code: '+1869', country: 'Saint Kitts and Nevis' },
    { code: '+1876', country: 'Jamaica' }
  ],
  'Europe': [
    { code: '+44', country: 'United Kingdom' },
    { code: '+49', country: 'Germany' },
    { code: '+33', country: 'France' },
    { code: '+39', country: 'Italy' },
    { code: '+34', country: 'Spain' },
    { code: '+31', country: 'Netherlands' },
    { code: '+32', country: 'Belgium' },
    { code: '+41', country: 'Switzerland' },
    { code: '+46', country: 'Sweden' },
    { code: '+47', country: 'Norway' },
    { code: '+45', country: 'Denmark' },
    { code: '+358', country: 'Finland' },
    { code: '+43', country: 'Austria' },
    { code: '+36', country: 'Hungary' },
    { code: '+48', country: 'Poland' },
    { code: '+420', country: 'Czech Republic' },
    { code: '+421', country: 'Slovakia' },
    { code: '+385', country: 'Croatia' },
    { code: '+386', country: 'Slovenia' },
    { code: '+40', country: 'Romania' },
    { code: '+359', country: 'Bulgaria' },
    { code: '+30', country: 'Greece' },
    { code: '+351', country: 'Portugal' },
    { code: '+352', country: 'Luxembourg' },
    { code: '+353', country: 'Ireland' },
    { code: '+354', country: 'Iceland' },
    { code: '+355', country: 'Albania' },
    { code: '+381', country: 'Serbia' },
    { code: '+382', country: 'Montenegro' },
    { code: '+383', country: 'Kosovo' },
    { code: '+387', country: 'Bosnia and Herzegovina' },
    { code: '+389', country: 'Macedonia' },
    { code: '+373', country: 'Moldova' },
    { code: '+380', country: 'Ukraine' },
    { code: '+375', country: 'Belarus' },
    { code: '+371', country: 'Latvia' },
    { code: '+370', country: 'Lithuania' },
    { code: '+372', country: 'Estonia' },
    { code: '+7', country: 'Russia' },
    { code: '+90', country: 'Turkey' },
    { code: '+356', country: 'Malta' },
    { code: '+357', country: 'Cyprus' },
    { code: '+376', country: 'Andorra' },
    { code: '+377', country: 'Monaco' },
    { code: '+378', country: 'San Marino' },
    { code: '+379', country: 'Vatican City' },
    { code: '+423', country: 'Liechtenstein' }
  ],
  'Middle East': [
    { code: '+971', country: 'United Arab Emirates' },
    { code: '+966', country: 'Saudi Arabia' },
    { code: '+974', country: 'Qatar' },
    { code: '+973', country: 'Bahrain' },
    { code: '+968', country: 'Oman' },
    { code: '+965', country: 'Kuwait' },
    { code: '+962', country: 'Jordan' },
    { code: '+961', country: 'Lebanon' },
    { code: '+972', country: 'Israel' },
    { code: '+970', country: 'Palestine' },
    { code: '+964', country: 'Iraq' },
    { code: '+963', country: 'Syria' },
    { code: '+967', country: 'Yemen' },
    { code: '+98', country: 'Iran' }
  ],
  'Africa': [
    { code: '+27', country: 'South Africa' },
    { code: '+20', country: 'Egypt' },
    { code: '+234', country: 'Nigeria' },
    { code: '+254', country: 'Kenya' },
    { code: '+233', country: 'Ghana' },
    { code: '+212', country: 'Morocco' },
    { code: '+216', country: 'Tunisia' },
    { code: '+213', country: 'Algeria' },
    { code: '+225', country: 'Ivory Coast' },
    { code: '+237', country: 'Cameroon' },
    { code: '+251', country: 'Ethiopia' },
    { code: '+255', country: 'Tanzania' },
    { code: '+256', country: 'Uganda' },
    { code: '+260', country: 'Zambia' },
    { code: '+263', country: 'Zimbabwe' },
    { code: '+267', country: 'Botswana' },
    { code: '+268', country: 'Eswatini' },
    { code: '+269', country: 'Comoros' },
    { code: '+291', country: 'Eritrea' },
    { code: '+297', country: 'Aruba' },
    { code: '+298', country: 'Faroe Islands' },
    { code: '+299', country: 'Greenland' },
    { code: '+350', country: 'Gibraltar' },
    { code: '+290', country: 'Saint Helena' },
    { code: '+291', country: 'Eritrea' },
    { code: '+297', country: 'Aruba' },
    { code: '+298', country: 'Faroe Islands' },
    { code: '+299', country: 'Greenland' },
    { code: '+350', country: 'Gibraltar' }
  ],
  'South America': [
    { code: '+55', country: 'Brazil' },
    { code: '+54', country: 'Argentina' },
    { code: '+56', country: 'Chile' },
    { code: '+57', country: 'Colombia' },
    { code: '+58', country: 'Venezuela' },
    { code: '+51', country: 'Peru' },
    { code: '+593', country: 'Ecuador' },
    { code: '+595', country: 'Paraguay' },
    { code: '+598', country: 'Uruguay' },
    { code: '+591', country: 'Bolivia' },
    { code: '+592', country: 'Guyana' },
    { code: '+594', country: 'French Guiana' },
    { code: '+597', country: 'Suriname' }
  ]
};

const SignUpPage = () => {
  const navigate = useNavigate();
  const { error } = useAuthContext();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  
  const [formData, setFormData] = useState({
    title: 'mr', // Default title
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    username: '',
    password: '',
    confirmPassword: ''
  });

  // Signup is locked unless the URL has a valid purchase_key (email create-account link).
  const [purchaseKey] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return (params.get('purchase_key') || '').trim();
  });
  const [unlockState, setUnlockState] = useState(
    purchaseKey ? 'checking' : 'locked'
  ); // checking | unlocked | invalid | locked | error
  const [purchaseEmail, setPurchaseEmail] = useState(null);
  const [unlockReason, setUnlockReason] = useState(null);
  
  // Validate purchase_key unlock + pre-fill email
  useEffect(() => {
    let cancelled = false;

    const unlock = async () => {
      if (!purchaseKey) {
        setUnlockState('locked');
        return;
      }

      try {
        setUnlockState('checking');
        setUnlockReason(null);
        const result = await validatePurchaseKey(purchaseKey);
        if (cancelled) return;

        if (!result.valid) {
          setUnlockState('invalid');
          setUnlockReason(result.reason || null);
          return;
        }

        const emailFromUrl = new URLSearchParams(window.location.search).get('email');
        const email = result.email || emailFromUrl || '';
        if (email) {
          setPurchaseEmail(String(email).trim().toLowerCase());
          setFormData((prev) => ({ ...prev, email: String(email).trim() }));
        }
        setUnlockState('unlocked');
      } catch (err) {
        if (cancelled) return;
        console.error('Purchase key unlock failed:', err);
        setUnlockState('error');
        setUnlockReason(null);
      }
    };

    unlock();
    return () => {
      cancelled = true;
    };
  }, [purchaseKey]);
  
  const [selectedCountryCode, setSelectedCountryCode] = useState('+1');
  const [selectedCountry, setSelectedCountry] = useState('United States');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const titles = [
    { value: 'mr', label: 'Mr.' },
    { value: 'mrs', label: 'Mrs.' },
    { value: 'ms', label: 'Ms.' },
    { value: 'miss', label: 'Miss' },
    { value: 'dr', label: 'Dr.' },
    { value: 'prof', label: 'Prof.' },
    { value: 'rev', label: 'Rev.' },
    { value: 'other', label: 'Other' }
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Update form data first
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // ตรวจสอบและตั้งค่าข้อผิดพลาดหากมีตัวเลขหรืออักขระพิเศษใน First Name หรือ Last Name
    if (name === 'firstName' || name === 'lastName') {
      if (/[^a-zA-Z\s]/.test(value)) { // Check for non-alphabet characters (excluding spaces)
        setValidationErrors(prev => ({
          ...prev,
          [name]: 'Numbers and special characters are not allowed'
        }));
      } else {
        // Clear the error if no digits or special characters are present
        if (validationErrors[name]) {
          setValidationErrors(prev => ({
            ...prev,
            [name]: ''
          }));
        }
      }
    } else if (validationErrors[name]) {
      // Clear other validation errors on change if they exist
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // คำนวณความแข็งแกร่งของรหัสผ่าน
    if (name === 'password') {
      const strength = calculatePasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const handleCountrySelect = (code, country) => {
    setSelectedCountryCode(code);
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchQuery('');
  };

  const filteredCountries = Object.entries(countryGroups).reduce((acc, [region, countries]) => {
    const filtered = countries.filter(country => 
      country.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.code.includes(searchQuery)
    );
    if (filtered.length > 0) {
      acc[region] = filtered;
    }
    return acc;
  }, {});

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const getPasswordStrengthText = (strength) => {
    switch (strength) {
      case 0: return { text: 'Very Weak', color: 'text-red-500' };
      case 1: return { text: 'Weak', color: 'text-red-500' };
      case 2: return { text: 'Fair', color: 'text-yellow-500' };
      case 3: return { text: 'Good', color: 'text-yellow-500' };
      case 4: return { text: 'Strong', color: 'text-green-500' };
      case 5: return { text: 'Very Strong', color: 'text-green-500' };
      default: return { text: '', color: '' };
    }
  };

  const validateForm = async () => {
    const errors = {};
    setIsChecking(true);
    
    // First Name validation (check for special characters and numbers)
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    } else if (/[^a-zA-Z\s]/.test(formData.firstName)) {
       errors.firstName = 'Numbers and special characters are not allowed';
    }
    
    // Last Name validation (check for special characters and numbers)
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData.lastName.length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    } else if (/[^a-zA-Z\s]/.test(formData.lastName)) {
       errors.lastName = 'Numbers and special characters are not allowed';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email';
    }
    
    // Phone number validation
    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number (minimum 10 digits)';
    }
    
    // Username validation
    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      errors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      errors.username = 'Username can only contain letters, numbers, and underscores';
    } else {
      // Check if username exists
      try {
        const q = query(collection(db, 'users'), where('username', '==', formData.username));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          errors.username = 'This username is already taken';
        }
      } catch (error) {
        // Only log in development for debugging
        if (process.env.NODE_ENV === 'development') {
          console.error('Error checking username:', error);
        }
      }
    }
    
    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (passwordStrength < 3) {
      errors.password = 'Password is too weak. Please use a stronger password';
    }
    
    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setIsChecking(false);
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (unlockState !== 'unlocked' || !purchaseKey) {
      setValidationErrors({
        form: 'Account creation requires a valid purchase link from your email.',
      });
      return;
    }
    
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Re-check the purchase key at submit time so a stale tab cannot race.
    try {
      const unlock = await validatePurchaseKey(purchaseKey);
      if (!unlock.valid) {
        setUnlockState('invalid');
        setValidationErrors({
          form: 'This purchase link is no longer valid. Contact support if you need help.',
        });
        return;
      }
      if (
        unlock.email &&
        formData.email.trim().toLowerCase() !== unlock.email
      ) {
        setValidationErrors({
          email: 'Email must match the email on your purchase.',
        });
        return;
      }
    } catch (err) {
      setValidationErrors({ form: 'Unable to verify purchase key. Please try again.' });
      return;
    }
    
    setIsLoading(true);

    try {
      // Execute reCAPTCHA v3 — invisible, score-based
      if (executeRecaptcha) {
        const recaptchaToken = await executeRecaptcha('signup');
        const controller = new AbortController();
        const captchaTimeout = setTimeout(() => controller.abort(), 8000);
        try {
          const verifyRes = await fetch(
            `${import.meta.env.VITE_FUNCTIONS_URL || 'https://us-central1-quantum-flo-auto-algo-d3c2b.cloudfunctions.net'}/verify_recaptcha`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ token: recaptchaToken, action: 'signup' }),
              signal: controller.signal,
            }
          );
          const verifyData = await verifyRes.json();
          if (!verifyData.success) {
            setValidationErrors({ form: 'Security check failed. Please try again.' });
            return;
          }
        } catch (captchaErr) {
          // On timeout or network error — fail open so legitimate users are not blocked
          console.warn('reCAPTCHA verification skipped:', captchaErr.message);
        } finally {
          clearTimeout(captchaTimeout);
        }
      }

      // Firebase Auth has client sign-up disabled (admin-restricted-operation).
      // Create the Auth user via the purchase-key-gated Cloud Function, then
      // show success and send the user to login.
      const phone = `${selectedCountryCode}${formData.phoneNumber}`.replace(/\s+/g, '');
      await signupService.signup({
        firstname: formData.firstName.trim(),
        lastname: formData.lastName.trim(),
        email: formData.email.trim().toLowerCase(),
        phone,
        password: formData.password,
        username: formData.username.trim(),
        purchase_key: purchaseKey,
      });

      setShowSuccessModal(true);
    } catch (error) {
      const apiMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      if (apiMessage) {
        setValidationErrors({ form: apiMessage });
      }
      // Only log in development for debugging
      if (import.meta.env.DEV) {
        console.error('Sign up error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <>
      <HeadersNonAuth />
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4 pt-32 pb-12">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
          <p className="text-gray-400">Join us to start trading</p>
        </div>

        {unlockState === 'checking' && (
          <div className="bg-gray-900 rounded-lg p-8 text-center text-gray-300">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-500 mx-auto mb-4" />
            Verifying your purchase link…
          </div>
        )}

        {(unlockState === 'locked' || unlockState === 'invalid' || unlockState === 'error') && (
          <div className="bg-gray-900 rounded-lg p-8 space-y-4 text-center">
            <div className="text-red-400 text-5xl" aria-hidden="true">🔒</div>
            <h2 className="text-xl font-semibold text-white">
              {unlockState === 'locked' ? 'Signup is locked' : 'Purchase link not valid'}
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              {unlockState === 'locked'
                ? 'Account creation requires the create-account link from your QFAA purchase email (it includes a purchase key).'
                : unlockState === 'error'
                  ? 'We could not verify this purchase link right now. Please try again shortly or contact support.'
                  : unlockReason === 'missing_email'
                    ? 'This purchase link has no email on file. Contact JTNC support to finish account setup.'
                    : unlockReason === 'unpaid'
                      ? 'This purchase key was found, but the payment is not active yet. Contact JTNC support if you already paid.'
                    : 'This purchase key was not found or the payment is not active. Use the link from your purchase confirmation email, or contact support.'}
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <Link
                to="/login"
                className="px-5 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium"
              >
                Go to Login
              </Link>
              <Link
                to="/pricing"
                className="px-5 py-2.5 rounded-lg bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 text-sm font-medium"
              >
                View Pricing
              </Link>
            </div>
          </div>
        )}

        {unlockState === 'unlocked' && (
        <form onSubmit={handleSubmit} className="bg-gray-900 rounded-lg p-8 space-y-6">
          {validationErrors.form && (
            <div className="p-3 rounded-lg bg-red-950/50 border border-red-700/50 text-red-300 text-sm">
              {validationErrors.form}
            </div>
          )}
          <div className="p-3 rounded-lg bg-emerald-950/40 border border-emerald-700/40 text-emerald-300 text-xs">
            Purchase verified{purchaseEmail ? ` for ${purchaseEmail}` : ''}. Complete the form to create your account.
          </div>

          {/* Name Fields */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  First Name
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
                  placeholder="John"
                />
                {validationErrors.firstName && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.firstName}</p>
                )}
              </div>

              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">
                  Last Name
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
                  placeholder="Doe"
                />
                {validationErrors.lastName && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.lastName}</p>
                )}
              </div>
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              readOnly={Boolean(purchaseEmail)}
              className={`w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none ${
                purchaseEmail ? 'opacity-80 cursor-not-allowed' : ''
              }`}
              placeholder="john@example.com"
            />
            {purchaseEmail && (
              <p className="text-gray-500 text-xs mt-1">
                Email is locked to the purchase on file for this link.
              </p>
            )}
            {validationErrors.email && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
            )}
          </div>
          
          {/* Phone Number with Enhanced Country Code Dropdown */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Phone Number
            </label>
            <div className="flex gap-2">
              <div className="relative w-48" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none flex items-center justify-between"
                >
                  <span>{selectedCountryCode} ({selectedCountry})</span>
                  {isDropdownOpen ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                
                {isDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-gray-800 rounded-lg shadow-lg border border-gray-700 max-h-96 overflow-hidden">
                    <div className="p-2 border-b border-gray-700">
                      <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search country or code..."
                          className="w-full bg-gray-700 text-white rounded-lg pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                    
                    <div className="overflow-y-auto max-h-80">
                      {Object.entries(filteredCountries).map(([region, countries]) => (
                        <div key={region}>
                          <div className="px-4 py-2 text-sm font-medium text-gray-400 bg-gray-900">
                            {region}
                          </div>
                          {countries.map((country) => (
                            <button
                              key={`${country.code}-${country.country}`}
                              type="button"
                              onClick={() => handleCountrySelect(country.code, country.country)}
                              className="w-full px-4 py-2 text-left text-white hover:bg-gray-700 focus:outline-none focus:bg-gray-700"
                            >
                              {country.code} {country.country}
                            </button>
                          ))}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
                placeholder="Enter phone number"
              />
            </div>
            <p className="text-gray-400 text-xs mt-1">
              Enter your phone number without the country code
            </p>
            {validationErrors.phoneNumber && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.phoneNumber}</p>
            )}
          </div>
          
          {/* Username */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Username
            </label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              className="w-full bg-gray-800 text-white rounded-lg px-4 py-3 border border-gray-700 focus:border-purple-500 focus:outline-none"
              placeholder="johndoe"
            />
            <p className="text-gray-400 text-xs mt-1">
              Use 3-20 characters. Only letters, numbers, and underscores allowed.
            </p>
            {validationErrors.username && (
              <p className="text-red-500 text-sm mt-1">{validationErrors.username}</p>
            )}
          </div>
          
          {/* Password */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="password">
              Password
            </label>
            <div className="relative">
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                aria-label="Toggle password visibility"
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {/* Password Strength Indicator */}
            {formData.password && (
              <div className="mt-2">
                <div className="flex items-center">
                  <div className="flex-1 h-2 bg-gray-700 rounded-full">
                    <div
                      className={`h-2 rounded-full ${getPasswordStrengthText(passwordStrength).color.replace('text-','bg-')}`}
                      style={{ width: `${(passwordStrength / 5) * 100}%` }}
                    />
                  </div>
                  <span className={`ml-2 text-sm ${getPasswordStrengthText(passwordStrength).color}`}>
                    {getPasswordStrengthText(passwordStrength).text}
                  </span>
                </div>
                <p className="text-gray-400 text-xs mt-1">
                  Password must contain at least 8 characters, including uppercase, lowercase, numbers, and special characters
                </p>
              </div>
            )}
            {validationErrors.password && <p className="text-red-500 text-xs italic mt-2">{validationErrors.password}</p>}
          </div>
          
          {/* Confirm Password */}
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="confirmPassword">
              Confirm Password
            </label>
            <div className="relative">
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="••••••••"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
              />
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                aria-label="Toggle confirm password visibility"
              >
                {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
            {validationErrors.confirmPassword && <p className="text-red-500 text-xs italic mt-2">{validationErrors.confirmPassword}</p>}
          </div>
          
          {/* Error Message */}
          {(error || validationErrors.form) && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 rounded-lg p-3 text-sm">
              {validationErrors.form || error}
            </div>
          )}
          
          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading || isChecking}
            className="w-full bg-purple-500 text-white rounded-lg py-3 font-medium hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Creating Account...' : isChecking ? 'Checking...' : 'Sign Up'}
          </button>
          
          {/* Login Link */}
          <p className="text-center text-gray-400 text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-purple-500 hover:text-purple-400">
              Sign In
            </Link>
          </p>
        </form>
        )}
      </div>
    </div>

      {showSuccessModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="signup-success-title"
        >
          <div className="w-full max-w-md rounded-xl border border-gray-700 bg-gray-900 p-6 shadow-xl">
            <h2 id="signup-success-title" className="text-xl font-semibold text-white mb-2">
              Account created successfully
            </h2>
            <p className="text-gray-400 text-sm mb-6">
              Your account is ready. Sign in with the email and password you just created.
            </p>
            <button
              type="button"
              className="w-full bg-purple-500 text-white rounded-lg py-3 font-medium hover:bg-purple-600 transition-colors"
              onClick={() => navigate('/login', { replace: true })}
            >
              Go to Login
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SignUpPage; 